import { useEffect, useRef, useState } from "react";

import {
  OutgoingIntakesAPI,
  type IntakeEmailPreview
} from "@/api/outgoingIntakesApi";
import type { OutgoingIntake } from "@/api/types/outgoingIntake";

export const EMAIL_PREVIEW_DEBOUNCE_MS = 400;

type Args = {
  intake: OutgoingIntake | null;
  /** Fetch only while the preview is on screen. */
  enabled: boolean;
  title: string;
  note: string;
};

export type EmailPreviewState = {
  /** The last good render; kept while a newer one loads. */
  preview: IntakeEmailPreview | null;
  loading: boolean;
  error: boolean;
};

/**
 * The real request email for the draft, rendered by the backend with the
 * unsaved note and title. Edits re-render after a short pause; the saved
 * questions re-render when they change.
 */
export const useEmailPreview = ({
  intake,
  enabled,
  title,
  note
}: Args): EmailPreviewState => {
  const [preview, setPreview] = useState<IntakeEmailPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seq = useRef(0);

  const clientId = intake?.foreign_national_id;
  const intakeId = intake?.outgoing_intake_id;
  const savedSchema = intake?.schema;

  useEffect(() => {
    setPreview(null);
  }, [intakeId]);

  useEffect(() => {
    if (!enabled || !clientId || !intakeId) return undefined;
    seq.current += 1;
    const id = seq.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      OutgoingIntakesAPI.emailPreview(clientId, intakeId, {
        message: note,
        title
      })
        .then((next) => {
          if (id !== seq.current) return;
          setPreview(next);
          setError(false);
        })
        .catch(() => {
          if (id === seq.current) setError(true);
        })
        .finally(() => {
          if (id === seq.current) setLoading(false);
        });
    }, EMAIL_PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, clientId, intakeId, note, title, savedSchema]);

  return { preview, loading, error };
};
